import { useLocation } from './url'

/**
 * Contentful Live Preview inspector data attributes
 */
export type InspectorTags = {
  'data-contentful-entry-id'?: string
  'data-contentful-field-id'?: string
  'data-contentful-asset-id'?: string
}

export const PAGE_TYPES = [
  'landingPage',
  'blogPage',
  'blogAuthor',
  'resourcePage',
  'resourceVideoPage',
  'helpArticle',
  'events',
]

type UseInspectorPropsParams =
  // Require either entryId or assetId,
  ({ entryId?: string; assetId?: never } | { assetId?: string; entryId?: never }) & {
    /**
     * A list of Contentful entry types to restrict the inspector mode to
     *
     * e.g. 'sectionSetOfImages', 'blogPost', etc.
     *
     * You can also set 'pages' to restrict to page-level entries like 'landingPage', 'blogPage', etc.
     *
     * If not provided, inspector mode will be enabled for all entry types.
     */
    restrictedTypes?: string[] | null
  }

export function getEntryInspectorProps({ entryId, fieldId }: { entryId?: string; fieldId: string }): InspectorTags {
  if (!entryId) return {}
  return { 'data-contentful-entry-id': entryId, 'data-contentful-field-id': fieldId }
}

export function getAssetInspectorProps({ assetId }: { assetId?: string }): InspectorTags {
  if (!assetId) return {}
  return { 'data-contentful-asset-id': assetId }
}

/**
 * A hook that returns a function to tag elements for Contentful's Live Preview inspector mode.
 */
export function useInspectorProps({ entryId, assetId, restrictedTypes }: UseInspectorPropsParams) {
  const { path, searchParams } = useLocation()
  const getEmptyInspectorProps = () => ({}) as InspectorTags
  if (path !== '/preview/') return getEmptyInspectorProps

  let getInspectorProps
  if (entryId) {
    getInspectorProps = (props: { fieldId: string }) => getEntryInspectorProps({ entryId, ...props })
  } else if (assetId) {
    getInspectorProps = () => getAssetInspectorProps({ assetId })
  } else {
    // If neither entryId nor assetId is provided, return an empty function
    return getEmptyInspectorProps
  }

  // Check if the current entry type matches the provided entry type
  const currentEntryType = searchParams.get('type')
  const typesToCheck = restrictedTypes || []
  if (restrictedTypes?.includes('pages')) {
    typesToCheck.push(...PAGE_TYPES)
  }
  if (!typesToCheck.includes(currentEntryType || '')) return getEmptyInspectorProps

  return getInspectorProps
}
